/**
 * Alexia Shop - Auth Store
 * =========================
 * Pinia store for authentication state.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api/client'
import type { User, LoginForm, RegisterForm } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  // ============================================
  // STATE
  // ============================================
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const isChecked = ref(false)
  const error = ref<string | null>(null)

  // ============================================
  // GETTERS
  // ============================================
  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.is_admin ?? false)
  const fullName = computed(() => user.value?.full_name ?? '')

  // ============================================
  // ACTIONS
  // ============================================

  /**
   * Check if user is authenticated (on app load)
   */
  async function checkAuth(): Promise<void> {
    if (isChecked.value) return

    try {
      const response = await api.get<User>('/auth/me')
      console.log('checkAuth response:', response.data)
      if (response.data.success && response.data.data) {
        user.value = response.data.data
        console.log('User set:', user.value, 'isAdmin:', user.value?.is_admin)
      }
    } catch (err) {
      console.log('checkAuth error:', err)
      user.value = null
    } finally {
      isChecked.value = true
    }
  }

  /**
   * Login with email and password
   */
  async function login(form: LoginForm): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<User>('/auth/login', form)
      console.log('Login response:', response.data)
      if (response.data.success && response.data.data) {
        user.value = response.data.data
        console.log('Login user set:', user.value, 'isAdmin:', user.value?.is_admin)
        return true
      }
      error.value = response.data.message || 'Login failed'
      return false
    } catch (err: any) {
      console.log('Login error:', err)
      error.value = err.response?.data?.message || 'An error occurred'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Register new user
   */
  async function register(form: RegisterForm): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<User>('/auth/register', form)
      if (response.data.success && response.data.data) {
        user.value = response.data.data
        return true
      }
      error.value = response.data.message || 'Registration failed'
      return false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'An error occurred'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Logout user
   */
  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout')
    } catch {
      // Ignore errors
    } finally {
      user.value = null
    }
  }

  /**
   * Request password reset
   */
  async function forgotPassword(email: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<null>('/auth/forgot-password', { email })
      return response.data.success ?? false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'An error occurred'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update user profile
   */
  async function updateProfile(data: Partial<User>): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.put<User>('/auth/profile', data)
      if (response.data.success && response.data.data) {
        user.value = response.data.data
        return true
      }
      error.value = response.data.message || 'Update failed'
      return false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'An error occurred'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clear error message
   */
  function clearError(): void {
    error.value = null
  }

  /**
   * Set user directly (for OAuth flows)
   */
  function setUser(userData: User): void {
    user.value = userData
    isChecked.value = true
  }

  /**
   * Fetch/refresh current user data
   */
  async function fetchUser(): Promise<void> {
    try {
      const response = await api.get<User>('/auth/me')
      if (response.data.success && response.data.data) {
        user.value = response.data.data
      } else {
        // Session invalid
        user.value = null
      }
    } catch {
      // Session expired or error - clear user
      user.value = null
    } finally {
      isChecked.value = true
    }
  }

  return {
    // State
    user,
    isLoading,
    isChecked,
    error,
    // Getters
    isAuthenticated,
    isAdmin,
    fullName,
    // Actions
    checkAuth,
    login,
    register,
    logout,
    forgotPassword,
    updateProfile,
    clearError,
    setUser,
    fetchUser,
  }
})
